import { useCallback } from 'react'

import { atom, useAtomValue, useSetAtom } from 'jotai'

import { runnerAtom } from '@/store/runner.store'

import {
  createRundownRunner,
  endRunnerTimesnap,
  pauseRunnerTimesnap,
  startRunnerTimesnap,
  startRunnerTimesnapOnPreviousCue,
  unsetRundownRunner,
  updateRundownCue,
} from '../../../firestore'
import { useCuesDerived } from '../cue/cue.derived'
import { getCue } from '../data/cues.store'
import { requireRundown } from '../data/rundown.store'

export const isRunnerLoadingAtom = atom<boolean>(false)

export function useTransportActions() {
  const runner = useAtomValue(runnerAtom)

  const { firstCueId } = useCuesDerived()

  const setLoading = useSetAtom(isRunnerLoadingAtom)

  async function onPlay() {
    const rundown = requireRundown()
    setLoading(true)
    await startRunnerTimesnap(rundown.id, rundown.runnerId).finally(() =>
      setLoading(false),
    )
  }

  async function onPause() {
    const rundown = requireRundown()
    setLoading(true)
    await pauseRunnerTimesnap(rundown.id, rundown.runnerId).finally(() =>
      setLoading(false),
    )
  }

  async function onPrev() {
    const rundown = requireRundown()
    setLoading(true)
    await startRunnerTimesnapOnPreviousCue(
      rundown.id,
      rundown.runnerId,
    ).finally(() => setLoading(false))
  }

  async function onNext() {
    const rundown = requireRundown()
    if (!runner) return
    setLoading(true)
    await startRunnerTimesnap(
      rundown.id,
      rundown.runnerId,
      runner.nextCueId,
    ).finally(() => setLoading(false))
  }

  async function onCreateRunner() {
    const rundown = requireRundown()
    setLoading(true)
    await createRundownRunner(rundown.id).finally(() => setLoading(false))
  }

  async function onUnsetRunner() {
    const rundown = requireRundown()
    setLoading(true)
    await unsetRundownRunner(rundown.id).finally(() => setLoading(false))
  }

  const onEndRunner = useCallback(async () => {
    const rundown = requireRundown()
    setLoading(true)
    await endRunnerTimesnap(rundown.id, rundown.runnerId).finally(() =>
      setLoading(false),
    )
  }, [setLoading])

  async function onTimeOperation(amount) {
    const rundown = requireRundown()
    if (!runner) return
    setLoading(true)

    const runningCueId = runner.timesnap.cueId
    const runningCue = runningCueId ? getCue(runningCueId) : undefined
    if (!runningCue) {
      setLoading(false)
      return
    }

    const duration = Math.max(runningCue.duration + amount, 0)
    const isFirstCue = runner.timesnap.cueId === firstCueId
    await updateRundownCue(
      rundown.id,
      runner.timesnap.cueId,
      { duration },
      isFirstCue,
    ).finally(() => setLoading(false))
  }

  return {
    onPlay,
    onPause,
    onPrev,
    onNext,
    onCreateRunner,
    onUnsetRunner,
    onEndRunner,
    onTimeOperation,
  }
}
